import React from 'react';
import Input from '../../shared/Input';
import SearchSelect from '../../shared/SearchSelect';

const specialties = [
  'Weddings',
  'Corporate Events',
  'Birthday Parties',
  'Graduations',
  'Anniversaries',
  'Baby Showers',
  'Bridal Showers',
  'Holiday Parties',
  'Engagement Parties',
  'Retirement Parties',
  'Gender Reveal Parties',
  'Conference & Seminars',
  'Trade Shows',
  'Fundraising Events',
  'Charity Events',
  'Sports Events',
  'Cultural Events',
  'Religious Ceremonies',
  'Launch Parties',
  'Product Launches',
  'Networking Events',
  'Team Building',
  'Company Retreats',
  'Awards Ceremonies',
  'Gala Events',
  'Festivals',
  'Concerts',
  'Exhibitions',
  'Other'
];

const Step1BusinessInfo = ({
  formData,
  errors,
  serviceCategories,
  categoriesLoading,
  categoriesError,
  handleInputChange,
  handleSpecialtyToggle
}) => {
  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2 sm:mb-4">Business Information</h3>
        <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6">
          Tell us about your business to help us verify your credentials.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <div>
          <Input
            label="Business Name *"
            value={formData.business_name}
            onChange={(e) => handleInputChange('business_name', e.target.value)}
            error={errors.business_name}
            placeholder="Enter your business name"
          />
        </div>

        <div>
          <SearchSelect
            label="Service Category"
            value={formData.service_categories}
            onChange={(value) => handleInputChange('service_categories', value)}
            options={serviceCategories.map(category => ({ 
              value: category.id, 
              label: category.category_name 
            }))}
            placeholder="Select a service category"
            error={errors.service_categories}
            required
            multiple={false}
            searchable={true}
            loading={categoriesLoading}
            emptyMessage={categoriesError ? "Failed to load categories" : "No categories found"}
          />
          <p className="mt-1 text-xs sm:text-sm text-gray-500">
            Select the primary service category for your business
          </p>
        </div>
      </div>

      <div>
        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
          Business Description *
        </label>
        <textarea
          value={formData.business_description}
          onChange={(e) => handleInputChange('business_description', e.target.value)}
          rows={4}
          maxLength={500}
          className={`w-full px-3 py-2 text-sm sm:text-base border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            errors.business_description ? 'border-red-300' : 'border-gray-300'
          }`}
          placeholder="Describe your business, services, and what makes you unique..."
        />
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mt-1 gap-1 sm:gap-0">
          {errors.business_description && (
            <p className="text-xs sm:text-sm text-red-600">{errors.business_description}</p>
          )}
          <p className={`text-xs sm:text-sm ml-auto ${
            formData.business_description.length > 450 
              ? 'text-orange-500' 
              : formData.business_description.length > 500 
                ? 'text-red-500' 
                : 'text-gray-500'
          }`}>
            {formData.business_description.length}/500 characters
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <div>
          <SearchSelect
            label="Years of Experience"
            value={formData.years_experience}
            onChange={(value) => handleInputChange('years_experience', value)}
            options={[
              { value: '0-1', label: '0-1 years' },
              { value: '2-3', label: '2-3 years' },
              { value: '4-5', label: '4-5 years' },
              { value: '6-10', label: '6-10 years' },
              { value: '10+', label: '10+ years' }
            ]}
            placeholder="Select experience"
            error={errors.years_experience}
            required
          />
        </div>

        <div>
          <Input
            label="Team Size"
            type="number"
            value={formData.team_size}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^\d+$/.test(value)) {
                handleInputChange('team_size', value);
              }
            }}
            onKeyDown={(e) => {
              if (!/[0-9]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                e.preventDefault();
              }
            }}
            onPaste={(e) => {
              const paste = e.clipboardData.getData('text');
              if (!/^\d+$/.test(paste)) {
                e.preventDefault();
              }
            }}
            placeholder="e.g., 5"
            min="1"
            className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
          Specialties * (Select all that apply)
        </label>
        
        {/* Select All Checkbox */}
        <div className="mb-3 pb-3 border-b border-gray-200">
          <label className="flex items-center cursor-pointer hover:bg-gray-50 p-2 -m-2 rounded-md transition-colors">
            <input
              type="checkbox"
              checked={formData.specialties.length === specialties.length && specialties.length > 0}
              onChange={(e) => {
                if (e.target.checked) {
                  // Select all specialties that aren't already selected
                  specialties.forEach(specialty => {
                    if (!formData.specialties.includes(specialty)) {
                      handleSpecialtyToggle(specialty);
                    }
                  });
                } else {
                  // Deselect all specialties
                  formData.specialties.forEach(specialty => {
                    handleSpecialtyToggle(specialty);
                  });
                }
              }}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded flex-shrink-0"
            />
            <span className="ml-2 text-sm font-medium text-gray-900">
              Select All
            </span>
            <span className="ml-2 text-xs text-gray-500">
              ({formData.specialties.length} of {specialties.length} selected)
            </span>
          </label>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
          {specialties.map(specialty => (
            <label key={specialty} className="flex items-center p-1.5 sm:p-0">
              <input
                type="checkbox"
                checked={formData.specialties.includes(specialty)}
                onChange={() => handleSpecialtyToggle(specialty)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded flex-shrink-0"
              />
              <span className="ml-2 text-xs sm:text-sm text-gray-700">{specialty}</span>
            </label>
          ))}
        </div>
        {errors.specialties && (
          <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.specialties}</p>
        )}
      </div>
    </div>
  );
};

export default Step1BusinessInfo;


